import { useState } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { MdContentCopy } from 'react-icons/md';
import { toast } from 'react-hot-toast';
import ClientInfoDrawer from '../drawers/ClientInfoDrawer';

type PhoneSet = {
    type: string;
    phone: string;
    id: number;
};
interface Props {
    patient?: any;
    metadata?: any;
    campaign?: any;
    application?: any;
    onDataUpdate?: () => void;
}

const ClientInfoTab: React.FC<Props> = ({ patient, metadata, campaign, application, onDataUpdate }) => {
    const person = patient?.person;

    const resolveStateName = (abbrev: string | undefined): string => {
        if (!abbrev) return '';
        const match = (metadata?.states || []).find(
            (s: { abbreviation: string; state: string }) =>
                s.abbreviation === abbrev || s.state === abbrev
        );
        return match?.state || abbrev;
    };
    let birthDate = person?.birth_date;
    let age: string | number = 'Unknown';
    if (birthDate) {
        birthDate = parseISO(person?.birth_date);
        const today = new Date();
        let years = today.getFullYear() - birthDate.getFullYear();
        const hasHadBirthdayThisYear =
            today.getMonth() > birthDate.getMonth() ||
            (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());
        if (!hasHadBirthdayThisYear) years -= 1;
        age = years;
    }

    let deathDate = person?.death_date;
    if (deathDate) {
        deathDate = parseISO(person?.death_date);
    }

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    return (
        <>
            <div className='p-[12px] pt-0'>
                <div className='flex justify-between'>
                    <h2 className='text-[22px]/[28px] font-bold'>
                        Client Info
                    </h2>
                    {/* Non-client campaigns (e.g. Emergency Grant Fund) have no
                        patient to edit, and the drawer crashes without one
                        (HHLTB-2960). */}
                    {patient && (
                        <button
                            className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                            onClick={() => {
                                setIsDrawerOpen(true);
                            }}
                        >
                            Edit
                        </button>
                    )}
                </div>
                {campaign?.direct_vendor_pay_only && (
                    <span className='mb-[16px] inline-flex rounded-full bg-yellow px-[16px] py-[6px] text-[14px]/[18px] font-semibold text-black'>
                        Direct Vendor Pay Only
                    </span>
                )}
                <div className='flex justify-between'>
                    <div className='grow'>
                        <div className='mb-[16px]'>
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Full Name
                            </label>
                            <div>
                                {person?.first_name} {person?.middle_name}{' '}
                                {person?.last_name}
                            </div>
                        </div>
                        {person?.nicknames && (
                            <div className='mb-[16px]'>
                                <label className='text-[12px]/[16px] text-[#949599]'>
                                    Nickname
                                </label>
                                <div>{person.nicknames}</div>
                            </div>
                        )}
                        {person?.search_names?.trim() && (
                            <div className='mb-[16px]'>
                                <label className='text-[12px]/[16px] text-[#949599]'>
                                    Search Names
                                </label>
                                <div>{person.search_names}</div>
                            </div>
                        )}
                        <div className='mb-[16px]'>
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Address
                            </label>
                            <div>{person?.address_1}</div>
                            <div>{person?.address_2}</div>
                            <div>
                                {person?.city}, {resolveStateName(person?.state)}{' '}
                                {person?.zip_code}
                            </div>
                        </div>
                        {person?.use_temporary_address && (
                            <div className='mb-[16px]'>
                                <label className='text-[12px]/[16px] text-[#949599]'>
                                    Temporary Address
                                </label>
                                <div>{person?.temporary_address_1}</div>
                                <div>{person?.temporary_address_2}</div>
                                <div>
                                    {person?.temporary_city},{' '}
                                    {resolveStateName(person?.temporary_state)}{' '}
                                    {person?.temporary_zip_code}
                                </div>
                            </div>
                        )}
                        <div className='mb-[16px]'>
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Email
                            </label>
                            <div className='flex items-center gap-2'>
                                <Link to={`mailto:${person?.email}`} className='text-[#00AEB3]'>
                                    {person?.email}
                                </Link>
                                {person?.email && (
                                    <button
                                        type='button'
                                        onClick={() => {
                                            navigator.clipboard.writeText(person?.email);
                                            toast.success('Email copied to clipboard');
                                        }}
                                        className='p-1 rounded transition-colors'
                                        style={{ color: '#00AEB3' }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#E6F7F8';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                        }}
                                        title='Copy email'
                                    >
                                        <MdContentCopy size={16} />
                                    </button>
                                )}
                            </div>
                        </div>
                        {(person?.phone_set || []).map((phone: PhoneSet, index: number) => (
                            <div key={phone.id || index} className='mb-[16px]'>
                                <label className='text-[12px]/[16px] text-[#949599]'>
                                    {phone?.type || 'Phone'}
                                </label>
                                <div>{phone.phone}</div>
                            </div>
                        ))}
                    </div>
                    <div className='grow'>
                        <div className='mb-[16px]'>
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Date of Birth
                            </label>
                            <div>{birthDate && format(birthDate, 'PPP')}</div>
                        </div>
                        <div className='mb-[16px]'>
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Age
                            </label>
                            <div>{age}</div>
                        </div>
                        {deathDate && (
                            <div className='mb-[16px]'>
                                <label className='text-[12px]/[16px] text-[#949599]'>
                                    Date of Death
                                </label>
                                <div>{format(deathDate, 'PPP')}</div>
                            </div>
                        )}
                        {person?.gender?.trim() && (
                            <div className='mb-[16px]'>
                                <label className='text-[12px]/[16px] text-[#949599]'>
                                    Gender
                                </label>
                                <div>{person.gender.trim()}</div>
                            </div>
                        )}
                        {person?.pronouns && (
                            <div className='mb-[16px]'>
                                <label className='text-[12px]/[16px] text-[#949599]'>
                                    Pronouns
                                </label>
                                <div>{person.pronouns}</div>
                            </div>
                        )}
                        {person?.marital_status && (
                            <div className='mb-[16px]'>
                                <label className='text-[12px]/[16px] text-[#949599]'>
                                    Marital Status
                                </label>
                                <div>{person.marital_status}</div>
                            </div>
                        )}
                        {person?.preferred_language && (
                            <div className='mb-[16px]'>
                                <label className='text-[12px]/[16px] text-[#949599]'>
                                    Preferred Language
                                </label>
                                <div>{person.preferred_language}</div>
                            </div>
                        )}
                        {patient?.ethnicity && (
                            <div className='mb-[16px]'>
                                <label className='text-[12px]/[16px] text-[#949599]'>
                                    Ethnicity
                                </label>
                                <div>{patient.ethnicity}</div>
                            </div>
                        )}
                        <div className='mb-[16px]'>
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Photo Sharing
                            </label>
                            <div>
                                {campaign?.report_optional_photo_consent
                                    ? 'Opted In'
                                    : 'Opted Out'}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <ClientInfoDrawer
                campaign={campaign}
                person={person}
                patient={patient}
                application={application}
                isOpen={isDrawerOpen}
                setIsOpen={setIsDrawerOpen}
                states={metadata?.states}
                maritalStatuses={metadata?.marital_statuses}
                preferredLanguages={metadata?.preferred_languages}
                ethnicities={metadata?.ethnicities}
                onDataSaved={onDataUpdate}
            />
        </>
    );
};

export default ClientInfoTab;
